/**
 * Line scanning with 1-based line numbers.
 *
 * Every parse error the user ever sees names a line ("Parsing failed at line 41"), so line
 * numbers are not an afterthought that gets bolted on when the error copy is written — they
 * are carried from the first split. `Cue.origin.sourceLine` and `Diagnostic.scope.sourceLine`
 * are both 1-based, so this is too.
 *
 * The adapter has already normalised `\r\n` and lone `\r` to `\n` and stripped the BOM
 * (adapter.md §5), so a parser never sees a carriage return. `scanLines` still tolerates one:
 * a parser can be handed text by a test or by another parser, and a stray `\r` producing a
 * cue whose text ends in an invisible control character is the kind of defect that survives
 * to production because it is invisible in a diff.
 */

export interface SourceLine {
  /** The line's text, with any trailing carriage return removed. */
  readonly text: string;
  /** 1-BASED line number, for parse errors and `Cue.origin.sourceLine`. */
  readonly no: number;
  /** Character offset of the line's first character within the whole document. */
  readonly offset: number;
}

/** Splits text into 1-based numbered lines. A trailing newline does NOT produce a final
 *  empty line — otherwise every file gains a phantom blank block at the end. */
export function scanLines(text: string): readonly SourceLine[] {
  const out: SourceLine[] = [];
  let offset = 0;
  let no = 1;
  let i = 0;
  while (i <= text.length) {
    const nl = text.indexOf('\n', i);
    const end = nl === -1 ? text.length : nl;
    let body = text.slice(i, end);
    if (body.endsWith('\r')) body = body.slice(0, -1);
    out.push({ text: body, no, offset });
    if (nl === -1) break;
    i = nl + 1;
    offset = i;
    no++;
  }
  // A file ending in '\n' yields a final empty line above; drop exactly that one.
  if (out.length > 1 && (out[out.length - 1] as SourceLine).text === '' && text.endsWith('\n')) {
    out.pop();
  }
  return out;
}

/** True for a line that is empty or contains only whitespace. */
export function isBlank(line: SourceLine): boolean {
  return line.text.trim() === '';
}

/**
 * Groups lines into blocks separated by one or more blank lines.
 *
 * Blank-line grouping is how SRT, WebVTT and SBV all delimit cues, and all three have files
 * in the wild with a blank line INSIDE a cue. That recovery is format-specific (it depends on
 * what a cue header looks like), so it lives in the parsers; this function does the split and
 * the parsers rejoin.
 */
export function blocksOf(lines: readonly SourceLine[]): readonly (readonly SourceLine[])[] {
  const out: SourceLine[][] = [];
  let current: SourceLine[] = [];
  for (const line of lines) {
    if (isBlank(line)) {
      if (current.length > 0) {
        out.push(current);
        current = [];
      }
      continue;
    }
    current.push(line);
  }
  if (current.length > 0) out.push(current);
  return out;
}

/**
 * Groups lines into blocks, KEEPING blank lines inside a block when the following content
 * does not start a new one.
 *
 * `startsBlock` is the format's own cue-header test. SRT is the case this exists for: a cue
 * whose text contains a blank line is common in files exported by tools that write a literal
 * empty subtitle line, and splitting on it produces a phantom cue whose "timestamp line" is
 * dialogue — which then fails to parse and takes a real cue with it.
 */
export function blocksWithRecovery(
  lines: readonly SourceLine[],
  startsBlock: (lines: readonly SourceLine[], at: number) => boolean,
): readonly (readonly SourceLine[])[] {
  const out: SourceLine[][] = [];
  let current: SourceLine[] | null = null;
  let pendingBlanks = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] as SourceLine;
    if (isBlank(line)) {
      if (current !== null) pendingBlanks++;
      continue;
    }
    if (current === null || (pendingBlanks > 0 && startsBlock(lines, i))) {
      if (current !== null) out.push(current);
      current = [line];
      pendingBlanks = 0;
      continue;
    }
    // A blank line that did NOT start a new block is content: it belongs to this cue's text.
    for (let b = 0; b < pendingBlanks; b++) {
      current.push({ text: '', no: line.no - pendingBlanks + b, offset: line.offset });
    }
    pendingBlanks = 0;
    current.push(line);
  }
  if (current !== null) out.push(current);
  return out;
}
